import trashIcon from '@SvgIcon/trash-b.svg'

import AwsS3 from '@uppy/aws-s3'
import Uppy from '@uppy/core'
import ImageEditor from '@uppy/image-editor'
import XHR from '@uppy/xhr-upload'
import svgIconHtml from '../../svgIconHtml'
import { Dashboard } from '../Uppy/Dashboard'

/**
 * Import styles:
 * <style src="@uppy/core/dist/style.css"></style>
 * <style src="@uppy/dashboard/dist/style.css"></style>
 * <style src="@uppy/image-editor/dist/style.css"></style>
 */

export default {
  name: 'UiFormUppy',

  components: {
    UppyDashboard: Dashboard,
  },

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  inject: {
    ngForm: {
      default: '',
    },
  },

  // language=HTML
  template: `
    <div class="ui-input">
      <input
        v-if="formFieldName"
        type="hidden"
        :name="formFieldName"
        :value="valueAsJson"
      />

      <div
        v-if="valueArray.length && showFilenames"
        class="warning callout"
        style="font-size: 14px; color: #000;"
      >
        <b>Files uploaded:</b>
        <ul style="margin-top: 0; margin-bottom: 0;">
          <li v-for="(val, idx) in valueArray" :key="idx">
            {{ val }}
            <button
              type="button"
              class="tiny hollow alert button"
              aria-label="Remove"
              @click="removeFile(val)"
              style="margin: 0"
            >
              ${svgIconHtml(trashIcon)}
            </button>
          </li>
        </ul>
      </div>

      <uppy-dashboard
        :uppy="uppy"
        :plugins="['ImageEditor']"
        :props="dashboardProps"
      />
    </div>
  `,

  props: {
    modelValue: {
      type: Array,
      default() {
        return []
      },
    },
    endpoint: {
      type: String,
      required: true,
    },
    isAwsEndpoint: {
      type: Boolean,
      default: false,
    },
    // backwards-compat. replaced by endpoint/isAwsEndpoint
    paramsUrl: {
      type: String,
      default: null,
    },
    formFieldName: {
      type: String,
    },
    minNumFiles: {
      type: Number,
      default: 0,
    },
    maxNumFiles: {
      type: Number,
      default: 1,
    },
    allowedTypes: {
      type: Array,
    },
    autoProceed: {
      type: Boolean,
      default: false,
    },
    showFilenames: {
      type: Boolean,
      default: true,
    },
    // bytes = {X}MB * 1E+6         (1E+6 === 1,000,000)
    maxFileSize: {
      type: Number,
      default: 50 * 1000000,
    },
  },

  data() {
    return {
      uppy: null,
    }
  },

  computed: {
    multipleFilesAllowed() {
      return this.maxNumFiles === -1 || this.maxNumFiles > 1
    },

    valueArray() {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue
      }
      if (this.modelValue) {
        return [this.modelValue]
      }
      return []
    },

    valueAsJson() {
      return JSON.stringify(this.valueArray)
    },

    shouldShowUploader() {
      return this.valueArray.length < this.maxNumFiles
    },

    dashboardProps() {
      const browseText = this.multipleFilesAllowed
        ? 'Drop files here, paste, or %{browse}'
        : 'Drop file here, paste, or %{browse}'

      return {
        theme: 'light',
        doneButtonHandler: null,
        showRemoveButtonAfterComplete: true,
        proudlyDisplayPoweredByUppy: false,
        locale: {
          strings: {
            dropPasteFiles: browseText,
          },
        },
      }
    },
  },

  methods: {
    removeFile(val) {
      const values = this.valueArray.slice()
      const idx = values.indexOf(val)
      if (idx !== -1) {
        values.splice(idx, 1)
        this.modelValue = values
        this.$emit('update:modelValue', values)
      }
    },
  },

  beforeDestroy() {
    this.uppy.close()
  },

  created() {
    const uppy = new Uppy({
      debug: true,
      autoProceed: this.autoProceed,
      allowMultipleUploadBatches: this.multipleFilesAllowed,
      restrictions: {
        maxFileSize: this.maxFileSize,
        maxNumberOfFiles: this.maxNumFiles === -1 ? null : this.maxNumFiles,
        minNumberOfFiles: this.minNumFiles,
        allowedFileTypes: this.allowedTypes,
      },
    }).use(ImageEditor, {
      quality: 0.8,
    })

    if (this.paramsUrl || this.isAwsEndpoint) {
      uppy.use(AwsS3, {
        getUploadParameters: (file) =>
          fetch(this.endpoint ? this.endpoint : this.paramsUrl, {
            method: 'post',
            headers: {
              accept: 'application/json',
              'content-type': 'application/json',
            },
            body: JSON.stringify({
              filename: file.name,
              contentType: file.type,
            }),
          })
            .then((response) => response.json())
            .then((data) => {
              if ('error' in data) {
                alert(data.error)
                return false
              }
              return {
                method: data.method,
                url: data.url,
                fields: data.fields,
              }
            }),
      })
    } else {
      uppy.use(XHR, {
        endpoint: this.endpoint,
        limit: 3,
      })
    }

    uppy.on('file-removed', (file, reason) => {
      if (reason === 'removed-by-user') {
        const values = this.valueArray.slice()
        const idx = values.indexOf(file.meta.filename)
        if (idx !== -1) {
          values.splice(idx, 1)
        }
        this.$emit('update:modelValue', values)
      }
    })

    uppy.on('upload-success', (file) => {
      let values = this.valueArray.slice()
      if (!this.multipleFilesAllowed) {
        values = [file.meta.filename]
      } else {
        values.push(file.meta.filename)
      }
      this.$emit('update:modelValue', values)
    })

    uppy.on('upload-error', (file, error, response) => {
      this.$emit('upload-error', file, error, response)
    })

    this.uppy = uppy
  },
}
